import React, { useState, useEffect } from 'react';
import '../styles/DeletedItems.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Table, 
  Space, 
  Button, 
  Input, 
  message, 
  Card, 
  Typography, 
  Tooltip,
  Modal,
  Select,
  Tag
} from 'antd';
import {
  UndoOutlined,
  SearchOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const { Title } = Typography;
const { confirm } = Modal;
const { Option } = Select;

const DeletedItems = () => {
  const { t } = useTranslation();
  const [deletedItems, setDeletedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState('createDate');
  const [sortOrder, setSortOrder] = useState('descend');

  const navigate = useNavigate();
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  const fetchDeletedItems = async () => {
    try {
      const response = await axios.get('https://app.hhoms.com/api/form/Libraries/list/deleted/0', {
        headers: {
          ApplicationToken: appToken,
          User: userData.id,
        },
      });
      
      const items = response.data.formLibraries || [];
      setDeletedItems(items);
    } catch (error) {
      console.error(t('deleted_items_load_error'), error);
      message.error(t('deleted_forms_load_error_message'));
      setDeletedItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeletedItems();
  }, [appToken, userData.id, t]);

  const handleRestore = async (id) => {
    confirm({
      title: t('confirm_restore_form'),
      icon: <ExclamationCircleOutlined style={{ color: '#52c41a' }} />,
      
      okText: t('yes_restore'),
      cancelText: t('cancel'),
      onOk: async () => {
        const hide = message.loading(t('restoring_form'), 0);
        try {
          await axios.put(`https://app.hhoms.com/api/form/Libraries/deleted/${id}/false`, 
            {},
            {
              headers: {
                ApplicationToken: appToken,
                User: userData.id,
              },
            }
          );
          hide();
          message.success(t('form_restored_successfully'));
          setDeletedItems(prevItems => prevItems.filter(item => item.id !== id));
        } catch (error) {
          hide();
          console.error(t('restore_error'), error);
          message.error(t('form_restore_error_message'));
        }
      },
    });
  };

  const columns = [
    {
      title: t('form_id'),
      dataIndex: 'formId',
      key: 'formId',
      width: 100,
      sorter: (a, b) => a.formId.localeCompare(b.formId),
    },
    {
      title: t('title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      filteredValue: [searchText],
      onFilter: (value, record) => 
        record.title.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('description'),
      dataIndex: 'formContent',
      key: 'formContent',
      width: 300,
      render: (formContent) => {
        try {
          const content = JSON.parse(formContent);
          return content.description || '-';
        } catch {
          return '-';
        }
      },
    },
    {
      title: t('created_date'),
      dataIndex: 'createDate',
      key: 'createDate',
      width: 180,
      render: (date) => moment(date).format('DD.MM.YYYY HH:mm'),
      sorter: (a, b) => moment(a.createDate).unix() - moment(b.createDate).unix(),
    },
    {
      title: t('updated_date'),
      dataIndex: 'editDate',
      key: 'editDate',
      width: 180,
      render: (date) => date ? moment(date).format('DD.MM.YYYY HH:mm') : '-',
      sorter: (a, b) => {
        if (!a.editDate) return -1;
        if (!b.editDate) return 1;
        return moment(a.editDate).unix() - moment(b.editDate).unix();
      },
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? t('active') : t('passive')}
        </Tag>
      ),
      filters: [
        { text: t('active'), value: true },
        { text: t('passive'), value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleRestore(record.id)}
          >
            {t('restore')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="archived-forms-container">
      <div style={{ padding: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Title level={4}>{t('deleted_forms')}</Title>
          
          <Input
            placeholder={t('search_form')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />

          <Table
            columns={columns}
            dataSource={deletedItems}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1300 }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => t('total_records', { count: total }),
            }}
            locale={{
              emptyText: t('no_deleted_forms'),
            }}
          />
        </Space>
      </div>
    </div>
  );
};

export default DeletedItems; 